import { FC } from 'react';

import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';
import Payment from '@/shared/types/order/payment.type';

import IssueRefundDialog from './components/IssueRefundDialog';
import PaymentsTable_v2 from './components/PaymentsTable_v2/PaymentsTable_v2';
import RefundConfirmationDialog from './components/RefundConfirmationDialog';
import Title from './components/Title';
import VoidTransactionConfirmationDialog from './components/VoidTransactionConfirmationDialog';
import VoidTransactionErrorsDialog from './components/VoidTransactionErrorsDialog';
import usePaymentsOperations from './hooks/usePaymentsOperations';

const Payments: FC = () => {
  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const { refreshTableIndex, onRefundPayment, onVoidTransaction } =
    usePaymentsOperations({
      dialog,
      onUpdateDialogState,
      onUpdateNotificationBar,
      onUpdateProgressDialog,
    });

  return (
    <div>
      <Title />

      <PaymentsTable_v2
        refreshTableIndex={refreshTableIndex}
        onIssueRefund={(payment) =>
          onUpdateDialogState(DialogName.IssueRefund, {
            data: payment,
            open: true,
            errors: [],
          })
        }
        onVoidTransaction={(payment) =>
          onUpdateDialogState(DialogName.VoidTransactionConfirmation, {
            data: payment,
            open: true,
          })
        }
      />

      {dialog?.issueRefund?.open && (
        <IssueRefundDialog
          payment={dialog.issueRefund.data as Payment}
          errors={dialog.issueRefund.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.IssueRefund, { open: false })
          }
          proceed={(payment, amount) => {
            onUpdateDialogState(DialogName.IssueRefundConfirmation, {
              data: {
                transactionId: payment.in_transaction_id,
                orderId: payment.in_order_id,
                amount,
              },
              open: true,
            });
          }}
        />
      )}

      {dialog?.issueRefundConfirmation?.open && (
        <RefundConfirmationDialog
          onClose={() =>
            onUpdateDialogState(DialogName.IssueRefundConfirmation, {
              open: false,
            })
          }
          proceed={() => {
            void onRefundPayment();
          }}
        />
      )}

      {dialog?.voidTransactionConfirmation?.open && (
        <VoidTransactionConfirmationDialog
          onClose={() =>
            onUpdateDialogState(DialogName.VoidTransactionConfirmation, {
              open: false,
            })
          }
          proceed={() => {
            void onVoidTransaction();
          }}
        />
      )}

      {dialog?.voidTransactionErrors?.open && (
        <VoidTransactionErrorsDialog
          errors={dialog.voidTransactionErrors.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.VoidTransactionErrors, {
              open: false,
            })
          }
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </div>
  );
};

export default Payments;
